import type { ReactNode } from 'react';
import { Button, Checkbox, Dialog, Select } from '@paige/ui';
import type { CardUse, DesktopPolicyState } from './desktop-types';
import { OffTimes } from './OffTimes';
import { scheduledOf, withSame, withTimes, withUse, type PolicyDraft } from './policy-draft';
import { availabilityWords, changedWords, zoneWords } from './policy-words';
import type { PolicyApi, SavePhase } from './use-policy-save';
import { usePolicyEditor, type PolicyEditorState } from './use-policy-editor';
import { cardName } from './gpu-words';
import './policy-editor.css';

const USE_WORDS: Record<CardUse, string> = {
  always: 'Always',
  never: 'Never',
  schedule: 'On a schedule',
};

const PHASE_WORDS: Partial<Record<SavePhase, string>> = {
  saving: 'Saving…',
  confirming: 'Sent to the machine. Waiting for it to show the change…',
  applied: 'Applied: the machine shows the new settings.',
  sent: 'Sent. The machine has not shown it yet; it applies as soon as it does.',
  waiting: 'Waiting for the machine: applies when it connects.',
  'too-old': 'Update the worker to change this from the web.',
};

interface DraftProps {
  draft: PolicyDraft;
  disabled: boolean;
  onDraft: (d: PolicyDraft) => void;
}

/** Each card: its name, Always / Never / On a schedule, and what it does now. */
function CardRows({
  state,
  draft,
  disabled,
  onDraft,
}: DraftProps & { state: DesktopPolicyState }): ReactNode {
  const zone = state.policy?.timeZone;
  return (
    <ul className="dg-edit__cards">
      {draft.cards.map((c) => (
        <li key={c.uuid} className="dg-edit__card">
          <strong>{cardName(c.name)}</strong>
          <Select
            aria-label={`${cardName(c.name)}: when Paige may use it`}
            value={c.use}
            disabled={disabled}
            onChange={(e) => onDraft(withUse(draft, c.uuid, e.target.value as CardUse))}
          >
            {(Object.keys(USE_WORDS) as CardUse[]).map((u) => (
              <option key={u} value={u}>
                {USE_WORDS[u]}
              </option>
            ))}
          </Select>
          <span className="dg-quiet">
            Now:{' '}
            {availabilityWords(
              state.cards.find((x) => x.uuid === c.uuid)?.availability ?? null,
              zone,
            )}
          </span>
        </li>
      ))}
    </ul>
  );
}

/**
 * The off times: one list naming every card on a schedule while "Same schedule for every card" is ticked (the tick
 * shows once two cards are on a schedule), else each card's own under its name.
 */
function Schedules({ draft, disabled, onDraft }: DraftProps): ReactNode {
  const scheduled = scheduledOf(draft);
  const first = scheduled[0];
  if (!first) return null;
  const shared = draft.same || scheduled.length === 1;
  return (
    <section className="dg-edit__times" aria-label="Off times">
      <h4 className="dg-edit__sub">Off times</h4>
      {scheduled.length > 1 ? (
        <Checkbox
          label="Same schedule for every card"
          checked={draft.same}
          disabled={disabled}
          onChange={(e) => onDraft(withSame(draft, e.target.checked))}
        />
      ) : null}
      {shared ? (
        <CardTimes
          title={`For ${scheduled.map((c) => cardName(c.name)).join(' and ')}`}
          off={first.off}
          disabled={disabled}
          onChange={(off) => onDraft(withTimes(draft, null, off))}
        />
      ) : (
        scheduled.map((c) => (
          <CardTimes
            key={c.uuid}
            title={cardName(c.name)}
            scope={cardName(c.name)}
            off={c.off}
            disabled={disabled}
            onChange={(off) => onDraft(withTimes(draft, c.uuid, off))}
          />
        ))
      )}
    </section>
  );
}

/** One list of off times under the cards it is for. */
function CardTimes({
  title,
  ...times
}: { title: string } & Parameters<typeof OffTimes>[0]): ReactNode {
  return (
    <div>
      <h4 className="dg-edit__sub">{title}</h4>
      <OffTimes {...times} />
    </div>
  );
}

type Editor = PolicyEditorState;

function Notices({ state, e }: { state: DesktopPolicyState; e: Editor }): ReactNode {
  return (
    <>
      {e.notice ? <p className="pl-notice">{e.notice}</p> : null}
      {state.problem ? <p className="pl-notice">{state.problem}</p> : null}
      {e.locked ? (
        <p className="pl-notice">Update the worker to change this from the web.</p>
      ) : null}
      {!state.connected && !e.locked ? (
        <p className="dg-quiet">
          The machine is away: a change waits and applies when it connects.
        </p>
      ) : null}
      {state.policy ? <p className="dg-quiet">{changedWords(state.policy)}.</p> : null}
    </>
  );
}

function Body({ e, state }: { e: Editor; state: DesktopPolicyState }): ReactNode {
  const off = e.locked || e.busy;
  return (
    <div className="dg-edit">
      <Notices state={state} e={e} />
      <Checkbox
        label="Pause this machine: no card is used until it is resumed"
        checked={e.draft.paused}
        disabled={off}
        onChange={(x) => e.setDraft({ ...e.draft, paused: x.target.checked })}
      />
      <CardRows state={state} draft={e.draft} disabled={off} onDraft={e.setDraft} />
      <Schedules draft={e.draft} disabled={off} onDraft={e.setDraft} />
      {e.problems.map((p) => (
        <p key={p} className="dg-error">
          {p}
        </p>
      ))}
      <p className={`dg-edit__phase dg-edit__phase--${e.s.phase}`} role="status">
        {e.s.phase === 'failed' || e.s.phase === 'changed'
          ? e.s.error
          : (PHASE_WORDS[e.s.phase] ?? '')}
      </p>
    </div>
  );
}

/**
 * Cards and schedule (docs/engineering/gpu-worker-cards-and-schedules.md, §4): pause the machine, each card Always /
 * Never / On a schedule, and the off times, in the machine's own zone. Saving sends it to the machine and reads back
 * until the machine shows it ("Applied"); an away machine waits; a worker too old is shown with every control off. A
 * change made elsewhere while it is open shows a notice and the new settings.
 */
export function PolicyEditor({
  name,
  state,
  api,
  onClose,
}: {
  name: string;
  state: DesktopPolicyState;
  api: PolicyApi;
  onClose: () => void;
}): ReactNode {
  const e = usePolicyEditor(state, api);
  const zone = state.policy?.timeZone;
  return (
    <Dialog
      open
      onOpenChange={(open) => !open && onClose()}
      title="Cards and schedule"
      description={`${name}: when Paige may use each card.${zone ? ` Times are ${zoneWords(zone)}.` : ''}`}
      size="lg"
      actions={
        <>
          <Button type="button" variant="secondary" onClick={onClose}>
            {e.s.phase === 'idle' ? 'Cancel' : 'Close'}
          </Button>
          <Button
            type="button"
            disabled={e.locked || e.busy || e.unchanged || e.problems.length > 0}
            onClick={() => void e.save()}
          >
            Save
          </Button>
        </>
      }
    >
      <Body e={e} state={state} />
    </Dialog>
  );
}
